import React, { createContext, useState, useContext, useEffect, useRef } from 'react';
import axios from 'axios';
import { isNativeApp, saveTokens, getAccessToken, getRefreshToken, clearTokens } from '../utils/tokenStorage';

const AuthContext = createContext();

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// On mobile (Capacitor), attach the stored bearer token to every request.
// On web, this is a no-op — cookies handle auth.
axios.interceptors.request.use(async (config) => {
  if (isNativeApp()) {
    const token = await getAccessToken();
    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }
  }
  // Always send credentials so web cookies flow normally.
  config.withCredentials = config.withCredentials !== false;
  return config;
});

export const formatApiErrorDetail = (detail) => {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
};

// Setup axios interceptor for auto-refresh
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error) => {
  failedQueue.forEach(prom => {
    if (error) prom.reject(error);
    else prom.resolve();
  });
  failedQueue = [];
};

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    // Skip auth endpoints and already-retried requests
    if (!error.response || error.response.status !== 401 || originalRequest._retry) {
      return Promise.reject(error);
    }
    // Never intercept auth endpoints
    const url = originalRequest.url || '';
    if (url.includes('/auth/login') || url.includes('/auth/me') || url.includes('/auth/refresh') || url.includes('/auth/logout')) {
      return Promise.reject(error);
    }
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      }).then(() => axios(originalRequest));
    }
    originalRequest._retry = true;
    isRefreshing = true;
    try {
      // On mobile, send the refresh token in body since cookies aren't reliable.
      let refreshBody = {};
      if (isNativeApp()) {
        const rt = await getRefreshToken();
        if (rt) refreshBody = { refresh_token: rt };
      }
      const refreshResp = await axios.post(`${API}/auth/refresh`, refreshBody, { withCredentials: true });
      // Save new access token on mobile
      if (isNativeApp() && refreshResp.data?.access_token) {
        await saveTokens({ access_token: refreshResp.data.access_token });
      }
      processQueue(null);
      return axios(originalRequest);
    } catch (refreshError) {
      processQueue(refreshError);
      // Only redirect if not already on login page
      if (!window.location.pathname.includes('/login')) {
        window.location.href = '/login';
      }
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  }
);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(data);
    } catch (error) {
      // If /me fails, try refreshing the token once
      try {
        await axios.post(`${API}/auth/refresh`, {}, { withCredentials: true });
        const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setUser(data);
      } catch (refreshErr) {
        setUser(false);
      }
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const { data } = await axios.post(
      `${API}/auth/login`,
      { email, password },
      { withCredentials: true }
    );
    // On mobile, persist the tokens returned in the body so subsequent requests
    // can send Authorization headers.
    if (isNativeApp() && data.access_token) {
      await saveTokens({ access_token: data.access_token, refresh_token: data.refresh_token });
    }
    setUser(data);
    return data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch {
      // ignore network errors during logout
    }
    if (isNativeApp()) await clearTokens();
    setUser(false);
  };

  const updateUser = (partial) => {
    setUser((prev) => (prev && typeof prev === 'object' ? { ...prev, ...partial } : prev));
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, checkAuth, updateUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
